import React from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import {
  sendTrainerChat,
  PAGE_ACTIONS,
  getCardAction,
  getCardView,
  formatTrainerValue,
} from "layouts/aibot/assistant";
import FormattedMessage from "layouts/aibot/FormattedMessage";
import brandLogo from "assets/images/logo.png";

const BRAND = "rgba(229, 10, 70, 1)";
const STORAGE_KEY = "dsf_aibot_conversations";

const createId = () =>
  globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createConversation = () => ({
  id: createId(),
  title: "New chat",
  updatedAt: Date.now(),
  messages: [],
  pending: null,
});

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.conversations) || !parsed.conversations.length) return null;
    return {
      conversations: parsed.conversations,
      activeId: parsed.activeId || parsed.conversations[0].id,
    };
  } catch {
    return null;
  }
};

const titleFromMessage = (text) => {
  const clean = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "New chat";
  return clean.length > 36 ? `${clean.slice(0, 36)}…` : clean;
};

const getCardButtons = (action) => {
  if (action === "apply") {
    return [
      { label: "Apply", message: "yes" },
      { label: "Cancel", message: "no" },
    ];
  }
  if (action === "undo" || action === "defaults" || action === "revert") {
    return [
      { label: "Yes", message: "yes" },
      { label: "No", message: "no" },
    ];
  }
  return [];
};

function Aibot() {
  const stored = React.useMemo(loadState, []);
  const initialConversation = React.useMemo(
    () => stored?.conversations?.[0] || createConversation(),
    [stored]
  );
  const [conversations, setConversations] = React.useState(
    () => stored?.conversations || [initialConversation]
  );
  const [activeId, setActiveId] = React.useState(() => stored?.activeId || initialConversation.id);
  const [draft, setDraft] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(() => window.innerWidth >= 768);
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const activeConversation = conversations.find((item) => item.id === activeId) || conversations[0];
  const messages = activeConversation?.messages || [];
  const pending = activeConversation?.pending ?? null;

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ conversations, activeId }));
  }, [conversations, activeId]);

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, isSending, activeId]);

  React.useEffect(() => {
    let wasDesktop = window.innerWidth >= 768;
    const onResize = () => {
      const desktop = window.innerWidth >= 768;
      if (desktop !== wasDesktop) {
        setShowHistory(desktop);
        wasDesktop = desktop;
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const updateConversation = React.useCallback((id, updater) => {
    setConversations((prev) =>
      prev.map((item) => (item.id === id ? { ...updater(item), updatedAt: Date.now() } : item))
    );
  }, []);

  const handleNewChat = () => {
    const next = createConversation();
    setConversations((prev) => [next, ...prev]);
    setActiveId(next.id);
    setDraft("");
    if (window.innerWidth < 768) setShowHistory(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleDeleteChat = (event, id) => {
    event.stopPropagation();
    setConversations((prev) => {
      const next = prev.filter((item) => item.id !== id);
      if (!next.length) {
        const fresh = createConversation();
        setActiveId(fresh.id);
        return [fresh];
      }
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  };

  const sendMessage = async (rawText) => {
    const text = String(rawText || "").trim();
    if (!text || isSending || !activeConversation) return;

    const userMessage = {
      id: createId(),
      role: "user",
      content: text,
      createdAt: Date.now(),
    };

    const conversationId = activeConversation.id;
    updateConversation(conversationId, (item) => ({
      ...item,
      title: item.messages.length ? item.title : titleFromMessage(text),
      messages: [...item.messages, userMessage],
    }));
    setDraft("");
    setIsSending(true);

    try {
      const result = await sendTrainerChat(text, pending);
      const cardAction = getCardAction(result.pending);
      const hasCardButtons = getCardButtons(cardAction).length > 0;
      const content = hasCardButtons && result.replyShort ? result.replyShort : result.reply;

      updateConversation(conversationId, (item) => ({
        ...item,
        pending: result.error ? item.pending ?? null : result.pending ?? null,
        messages: [
          ...item.messages,
          {
            id: createId(),
            role: "assistant",
            content,
            createdAt: Date.now(),
            card: hasCardButtons ? result.pending : null,
          },
        ],
      }));
    } catch {
      updateConversation(conversationId, (item) => ({
        ...item,
        messages: [
          ...item.messages,
          {
            id: createId(),
            role: "assistant",
            content: "Something went wrong while generating a reply. Please try again.",
            createdAt: Date.now(),
          },
        ],
      }));
    } finally {
      setIsSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage(draft);
    }
  };

  return (
    <DashboardLayout>
      <MDBox
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
          height: `calc(100vh - ${theme.spacing(6)})`,
          maxHeight: `calc(100vh - ${theme.spacing(6)})`,
          "@supports (height: 100dvh)": {
            height: `calc(100dvh - ${theme.spacing(6)})`,
            maxHeight: `calc(100dvh - ${theme.spacing(6)})`,
          },
        })}
      >
        <MDBox sx={{ flexShrink: 0 }}>
          <DashboardNavbar />
        </MDBox>
        <Card
          sx={{
            flex: 1,
            minHeight: 0,
            mt: { xs: 1, sm: 1.5, md: 2 },
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
            position: "relative",
            border: `1px solid ${BRAND}`,
          }}
        >
          {showHistory && (
            <MDBox
              onClick={() => setShowHistory(false)}
              sx={{
                display: { xs: "block", md: "none" },
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                zIndex: 11,
              }}
            />
          )}
          <MDBox
            sx={{
              position: { xs: "absolute", md: "relative" },
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: { xs: 12, md: 1 },
              width: { xs: 240, sm: 260, md: showHistory ? 260 : 0 },
              minWidth: { xs: 240, sm: 260, md: showHistory ? 260 : 0 },
              maxWidth: { xs: "84%", md: "none" },
              height: "100%",
              transform: {
                xs: showHistory ? "translateX(0)" : "translateX(-105%)",
                md: "none",
              },
              borderRight: showHistory ? "1px solid rgba(0,0,0,0.08)" : 0,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#f8f9fb",
              overflow: "hidden",
              pointerEvents: showHistory ? "auto" : "none",
              boxShadow: {
                xs: showHistory ? "8px 0 24px rgba(0,0,0,0.12)" : "none",
                md: "none",
              },
              transition: "width 180ms ease, min-width 180ms ease, transform 180ms ease",
            }}
          >
            <MDBox
              p={{ xs: 1.5, sm: 2 }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ flexShrink: 0 }}
            >
              <MDTypography variant="button" fontWeight="bold">
                Chats
              </MDTypography>
              <MDButton
                type="button"
                variant="contained"
                size="small"
                onClick={handleNewChat}
                sx={{
                  background: BRAND,
                  color: "#fff",
                  minWidth: 0,
                  px: 1.5,
                  "&:hover": { background: "rgba(229, 10, 70, 0.9)" },
                }}
              >
                New
              </MDButton>
            </MDBox>
            <Divider sx={{ m: 0 }} />
            <MDBox sx={{ flex: 1, overflowY: "auto", p: 1 }}>
              {conversations.map((item) => {
                const active = item.id === activeId;
                return (
                  <MDBox
                    key={item.id}
                    onClick={() => {
                      setActiveId(item.id);
                      if (window.innerWidth < 768) setShowHistory(false);
                    }}
                    display="flex"
                    alignItems="center"
                    sx={{
                      px: 1.25,
                      py: 1,
                      mb: 0.5,
                      borderRadius: "0.6rem",
                      cursor: "pointer",
                      backgroundColor: active ? BRAND : "transparent",
                      color: active ? "#fff" : "inherit",
                      "&:hover": {
                        backgroundColor: active ? BRAND : "rgba(229, 10, 70, 0.08)",
                      },
                    }}
                  >
                    <Icon sx={{ fontSize: 18, mr: 1, color: active ? "#fff" : BRAND }}>
                      chat_bubble
                    </Icon>
                    <MDBox sx={{ minWidth: 0, flex: 1 }}>
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          color: active ? "#fff" : "#344767",
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.title}
                      </MDTypography>
                    </MDBox>
                    <IconButton
                      size="small"
                      onClick={(event) => handleDeleteChat(event, item.id)}
                      sx={{ color: active ? "#fff" : "#7b809a", ml: 0.5 }}
                      aria-label="Delete chat"
                    >
                      <Icon sx={{ fontSize: 16 }}>delete</Icon>
                    </IconButton>
                  </MDBox>
                );
              })}
            </MDBox>
          </MDBox>

          <MDBox
            display="flex"
            flexDirection="column"
            sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}
          >
            <MDBox
              px={{ xs: 1.25, sm: 2 }}
              py={{ xs: 1, sm: 1.5 }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)", flexShrink: 0, minWidth: 0 }}
            >
              <MDBox display="flex" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setShowHistory((value) => !value)}
                  sx={{ mr: { xs: 0.5, sm: 1 }, color: BRAND, flexShrink: 0 }}
                  aria-label="Toggle chat history"
                >
                  <Icon>{showHistory ? "menu_open" : "menu"}</Icon>
                </IconButton>
                <MDAvatar
                  size="sm"
                  sx={{
                    backgroundColor: `${BRAND} !important`,
                    color: "#fff",
                    mr: { xs: 1, sm: 1.25 },
                    flexShrink: 0,
                  }}
                >
                  <Icon>smart_toy</Icon>
                </MDAvatar>
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography variant="button" fontWeight="bold" sx={{ display: "block" }}>
                    Live Coaching
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    display="block"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Daily Success Friend assistant
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>

            <MDBox
              ref={listRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                px: { xs: 1.25, sm: 2, md: 4 },
                py: { xs: 1.5, md: 3 },
                background:
                  "linear-gradient(180deg, rgba(229, 10, 70, 0.04) 0%, rgba(255,255,255,1) 140px)",
              }}
            >
              {!messages.length && !isSending ? (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  sx={{ minHeight: "100%" }}
                >
                  <MDAvatar
                    size="xl"
                    sx={{
                      backgroundColor: `${BRAND} !important`,
                      color: "#fff",
                      width: { xs: 56, sm: 72 },
                      height: { xs: 56, sm: 72 },
                      mb: { xs: 1.5, md: 2 },
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: 28, sm: 36 } }}>smart_toy</Icon>
                  </MDAvatar>
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
                  >
                    Hi, I&apos;m Live Coaching
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    color="text"
                    mt={1}
                    mb={{ xs: 2, md: 3 }}
                    px={1}
                    sx={{ maxWidth: 420 }}
                  >
                    Ask the coach trainer to change wording, settings, examples, or notes. Type yes
                    or no to apply a proposal.
                  </MDTypography>
                  <MDBox
                    display="flex"
                    flexWrap="wrap"
                    justifyContent="center"
                    gap={1}
                    px={1}
                    sx={{ width: "100%", maxWidth: 520 }}
                  >
                    {PAGE_ACTIONS.map((item) => (
                      <Chip
                        key={item.message}
                        label={item.label}
                        onClick={() => sendMessage(item.message)}
                        sx={{
                          backgroundColor: "#fff",
                          border: `1px solid ${BRAND}`,
                          color: BRAND,
                          maxWidth: "100%",
                          "&:hover": { backgroundColor: "rgba(229, 10, 70, 0.08)" },
                        }}
                      />
                    ))}
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1.5}>
                  {messages.map((message, index) => {
                    const isUser = message.role === "user";
                    const bubbleRadius = isUser
                      ? "1rem 1rem 0.25rem 1rem"
                      : "1rem 1rem 1rem 0.25rem";
                    const card = getCardView(message.card);
                    const cardAction = getCardAction(message.card);
                    const cardButtons = getCardButtons(cardAction);
                    const isLastMessage = index === messages.length - 1;
                    const showCardButtons =
                      !isUser && isLastMessage && pending && cardButtons.length > 0;
                    const beforeText = formatTrainerValue(card?.before);
                    const afterText = formatTrainerValue(card?.after);
                    const previewText = formatTrainerValue(card?.preview);
                    const explainText = formatTrainerValue(card?.explain);

                    return (
                      <MDBox
                        key={message.id}
                        display="flex"
                        alignItems="flex-start"
                        justifyContent={isUser ? "flex-end" : "flex-start"}
                        sx={{ maxWidth: "100%", minWidth: 0 }}
                      >
                        {!isUser && (
                          <MDAvatar
                            size="xs"
                            sx={{
                              backgroundColor: `${BRAND} !important`,
                              color: "#fff",
                              mr: 1,
                              mt: 0.5,
                              flexShrink: 0,
                            }}
                          >
                            <Icon sx={{ fontSize: 16 }}>smart_toy</Icon>
                          </MDAvatar>
                        )}
                        <MDBox
                          sx={{
                            maxWidth: {
                              xs: "calc(100% - 40px)",
                              sm: "88%",
                              md: "70%",
                            },
                            minWidth: 0,
                          }}
                        >
                          <MDBox
                            sx={{
                              px: { xs: 1.5, sm: 2 },
                              py: { xs: 1, sm: 1.25 },
                              borderRadius: bubbleRadius,
                              backgroundColor: isUser ? BRAND : "#fff",
                              color: isUser ? "#fff" : "#344767",
                              border: isUser ? 0 : "1px solid rgba(0,0,0,0.08)",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
                              whiteSpace: isUser ? "pre-wrap" : "normal",
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            <MDTypography
                              component="div"
                              variant="button"
                              sx={{
                                display: "block",
                                color: isUser ? "#fff" : "#344767",
                                fontWeight: 400,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {isUser ? (
                                message.content
                              ) : (
                                <FormattedMessage text={message.content} />
                              )}
                            </MDTypography>
                          </MDBox>
                          {!isUser && card && (card.title || beforeText || afterText) && (
                            <MDBox
                              mt={1}
                              p={1.5}
                              sx={{
                                backgroundColor: "#fff",
                                border: "1px solid rgba(0,0,0,0.08)",
                                borderRadius: "0.85rem",
                              }}
                            >
                              {card.title && (
                                <MDTypography variant="button" fontWeight="bold" display="block">
                                  {card.title}
                                </MDTypography>
                              )}
                              {explainText && (
                                <MDTypography
                                  component="div"
                                  variant="caption"
                                  color="text"
                                  display="block"
                                  mt={0.5}
                                >
                                  <FormattedMessage text={explainText} />
                                </MDTypography>
                              )}
                              {(beforeText || afterText) && (
                                <MDBox mt={1} display="flex" gap={1} flexWrap="wrap">
                                  {beforeText && (
                                    <MDBox
                                      flex={1}
                                      minWidth={{ xs: "100%", sm: 140 }}
                                      p={1}
                                      sx={{
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                        borderRadius: "0.5rem",
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        display="block"
                                      >
                                        Before
                                      </MDTypography>
                                      <MDTypography
                                        component="div"
                                        variant="caption"
                                        color="text"
                                        sx={{ overflowWrap: "anywhere" }}
                                      >
                                        <FormattedMessage text={beforeText} />
                                      </MDTypography>
                                    </MDBox>
                                  )}
                                  {afterText && (
                                    <MDBox
                                      flex={1}
                                      minWidth={{ xs: "100%", sm: 140 }}
                                      p={1}
                                      sx={{
                                        backgroundColor: "rgba(229, 10, 70, 0.06)",
                                        borderRadius: "0.5rem",
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        display="block"
                                      >
                                        After
                                      </MDTypography>
                                      <MDTypography
                                        component="div"
                                        variant="caption"
                                        color="text"
                                        sx={{ overflowWrap: "anywhere" }}
                                      >
                                        <FormattedMessage text={afterText} />
                                      </MDTypography>
                                    </MDBox>
                                  )}
                                </MDBox>
                              )}
                              {previewText && (
                                <MDBox mt={1}>
                                  <MDTypography variant="caption" color="text" fontWeight="medium">
                                    Preview
                                  </MDTypography>
                                  <MDTypography
                                    component="div"
                                    variant="caption"
                                    color="text"
                                    sx={{ overflowWrap: "anywhere" }}
                                  >
                                    <FormattedMessage text={previewText} />
                                  </MDTypography>
                                </MDBox>
                              )}
                            </MDBox>
                          )}
                          {showCardButtons && (
                            <MDBox display="flex" gap={1} mt={1} flexWrap="wrap">
                              {cardButtons.map((button) => (
                                <MDButton
                                  key={button.label}
                                  type="button"
                                  variant={button.message === "yes" ? "contained" : "outlined"}
                                  size="small"
                                  onClick={() => sendMessage(button.message)}
                                  disabled={isSending}
                                  sx={{
                                    background: button.message === "yes" ? BRAND : "#fff",
                                    color: button.message === "yes" ? "#fff" : BRAND,
                                    borderColor: BRAND,
                                    "&:hover": {
                                      background:
                                        button.message === "yes"
                                          ? "rgba(229, 10, 70, 0.9)"
                                          : "#fff",
                                    },
                                  }}
                                >
                                  {button.label}
                                </MDButton>
                              ))}
                            </MDBox>
                          )}
                        </MDBox>
                        {isUser && (
                          <MDAvatar
                            src={brandLogo}
                            alt="Daily Success Friend logo"
                            size="xs"
                            sx={{
                              ml: 1,
                              mt: 0.5,
                              flexShrink: 0,
                              backgroundColor: "transparent !important",
                              "& img": {
                                position: "absolute",
                                top: "-15%",
                                left: "-64%",
                                width: "430%",
                                height: "auto",
                                maxWidth: "none",
                                objectFit: "contain",
                              },
                            }}
                          />
                        )}
                      </MDBox>
                    );
                  })}
                  {isSending && (
                    <MDBox display="flex" alignItems="center" sx={{ minWidth: 0 }}>
                      <MDAvatar
                        size="xs"
                        sx={{
                          backgroundColor: `${BRAND} !important`,
                          color: "#fff",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }}>smart_toy</Icon>
                      </MDAvatar>
                      <MDBox
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderRadius: "1rem",
                          backgroundColor: "#fff",
                          border: "1px solid rgba(0,0,0,0.08)",
                          display: "flex",
                          gap: 0.75,
                        }}
                      >
                        {[0, 1, 2].map((dot) => (
                          <MDBox
                            key={dot}
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              backgroundColor: BRAND,
                              animation: "aibotPulse 1s ease-in-out infinite",
                              animationDelay: `${dot * 0.15}s`,
                              "@keyframes aibotPulse": {
                                "0%, 80%, 100%": { opacity: 0.3, transform: "translateY(0)" },
                                "40%": { opacity: 1, transform: "translateY(-3px)" },
                              },
                            }}
                          />
                        ))}
                      </MDBox>
                    </MDBox>
                  )}
                </MDBox>
              )}
            </MDBox>

            <MDBox
              px={{ xs: 1.25, sm: 2, md: 3 }}
              py={{ xs: 1.25, md: 2 }}
              sx={{ borderTop: "1px solid rgba(0,0,0,0.08)", flexShrink: 0 }}
            >
              <MDBox
                display="flex"
                flexWrap="wrap"
                gap={{ xs: 0.75, md: 1 }}
                mb={{ xs: 1, md: 1.5 }}
                sx={{
                  maxHeight: { xs: 72, sm: "none" },
                  overflowY: { xs: "auto", sm: "visible" },
                }}
              >
                {PAGE_ACTIONS.map((item) => (
                  <Chip
                    key={item.message}
                    size="small"
                    label={item.label}
                    onClick={() => sendMessage(item.message)}
                    disabled={isSending}
                    sx={{
                      backgroundColor: "rgba(229, 10, 70, 0.06)",
                      color: BRAND,
                      maxWidth: "100%",
                    }}
                  />
                ))}
              </MDBox>
              <MDBox
                display="flex"
                alignItems="flex-end"
                sx={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  borderRadius: "1rem",
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.75, sm: 1 },
                  backgroundColor: "#fff",
                  minWidth: 0,
                }}
              >
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Message Live Coaching..."
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isSending}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    minWidth: 0,
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.875rem", sm: "0.9rem" },
                    },
                  }}
                />
                <IconButton
                  type="button"
                  onClick={() => sendMessage(draft)}
                  disabled={!draft.trim() || isSending}
                  sx={{
                    ml: { xs: 0.5, sm: 1 },
                    flexShrink: 0,
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    backgroundColor: BRAND,
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(229, 10, 70, 0.9)" },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(229, 10, 70, 0.35)",
                      color: "#fff",
                    },
                  }}
                  aria-label="Send message"
                >
                  <Icon>send</Icon>
                </IconButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default Aibot;
